import {
  CalendarCheck,
  ChartLineUp,
  DeviceMobile,
  PlugsConnected,
  ShieldCheck,
} from '@phosphor-icons/react';
import { Link2, Brain, Rocket } from 'lucide-react';

export const SAAS_SIGNUP_URL = 'https://engagement.kolanut.ai/signup';

export const capabilities = [
  {
    Icon: PlugsConnected,
    title: 'Connect your stack',
    description:
      'Plug in your tools in minutes. One place for product, CRM, ads, and messaging so nothing falls through the cracks.',
    tags: ['Lead capture', 'One stack', 'Live context'],
    accent: {
      icon: 'bg-gradient-to-br from-sky-100 to-sky-50 ring-sky-200/90',
      iconColor: 'text-sky-700',
      card: 'border-sky-200/70 bg-gradient-to-br from-white to-sky-50/50',
      tag: 'border-sky-200 bg-sky-50 text-sky-800',
    },
  },
  {
    Icon: ChartLineUp,
    title: 'Convert leads',
    description:
      'Turn signups into paying customers with flows you control. Kolanut shows the next step. You approve it.',
    tags: ['Trials', 'Upgrade paths', 'You approve'],
    accent: {
      icon: 'bg-gradient-to-br from-violet-100 to-violet-50 ring-violet-200/90',
      iconColor: 'text-violet-700',
      card: 'border-violet-200/70 bg-gradient-to-br from-white to-violet-50/40',
      tag: 'border-violet-200 bg-violet-50 text-violet-800',
    },
  },
  {
    Icon: CalendarCheck,
    title: 'Follow up and book',
    description:
      'Nudge cold leads, reply faster, and get meetings on the calendar without living in five inboxes.',
    tags: ['Follow up', 'Demos booked', 'Faster replies'],
    accent: {
      icon: 'bg-gradient-to-br from-amber-100 to-amber-50 ring-amber-200/90',
      iconColor: 'text-amber-800',
      card: 'border-amber-200/70 bg-gradient-to-br from-white to-amber-50/45',
      tag: 'border-amber-200 bg-amber-50 text-amber-900',
    },
  },
  {
    Icon: ShieldCheck,
    title: 'Keep people paying',
    description:
      'Spot churn early, run save playbooks, and protect ARR before customers hit cancel.',
    tags: ['Churn alerts', 'ARR saved', 'Renewals'],
    accent: {
      icon: 'bg-gradient-to-br from-emerald-100 to-emerald-50 ring-emerald-200/90',
      iconColor: 'text-emerald-700',
      card: 'border-emerald-200/70 bg-gradient-to-br from-white to-emerald-50/45',
      tag: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    },
  },
  {
    Icon: DeviceMobile,
    title: 'Run it from your phone',
    description:
      'Your entire growth engine in one mobile app. Alerts, copilot, and approvals on web or phone.',
    tags: ['Live alerts', 'Copilot', 'Web + mobile'],
    accent: {
      icon: 'bg-gradient-to-br from-rose-100 to-rose-50 ring-rose-200/90',
      iconColor: 'text-rose-700',
      card: 'border-rose-200/70 bg-gradient-to-br from-white to-rose-50/40',
      tag: 'border-rose-200 bg-rose-50 text-rose-800',
    },
  },
];

export const integrations = [
  'Meta Ads',
  'Google Ads',
  'Email/SMS platforms',
  'Custom API connections',
  'CRM tools',
];

/** Logo catalog for how-it-works chips (same pattern as LeadGen TabbedFeatures) */
export const saasIntegrationsWithLogos = [
  { name: 'Meta', image: '/integrations/instagram.webp', alt: 'Meta Ads' },
  { name: 'Google Ads', image: '/integrations/google_ads.png', alt: 'Google Ads' },
  { name: 'Gmail', image: '/integrations/gmail.png', alt: 'Gmail' },
  { name: 'SMS', image: '/sms-logo.png', alt: 'SMS' },
];

export const logoBySaasIntegrationName = saasIntegrationsWithLogos.reduce((acc, item) => {
  acc[item.name] = item.image;
  return acc;
}, {});

export const faqItemsData = [
  {
    id: 1,
    question: 'What is the Company Brain?',
    answer:
      'The Company Brain is Kolanut\'s unified layer for your SaaS product and user data.\n\n• Connects analytics, CRM, ads, and messaging tools\n• Learns from user behavior and lifecycle events\n• Powers personalized campaigns, onboarding, and support\n• Keeps context across marketing, product, and success teams',
  },
  {
    id: 2,
    question: 'What do the AI agents actually do?',
    answer:
      'Kolanut runs specialized agents across your growth stack.\n\n• Marketing: campaigns and lifecycle messaging\n• Onboarding: sequences tailored to signup cohorts\n• Support: proactive outreach before churn signals spike\n• You approve strategy; agents execute within your rules',
  },
  {
    id: 3,
    question: 'Which tools can SaaS teams connect?',
    answer:
      'Kolanut integrates with the channels and systems you already use.\n\n• Meta Ads and Google Ads\n• Email and SMS platforms\n• CRM and product analytics tools\n• Custom API connections for proprietary stacks',
  },
  {
    id: 4,
    question: 'Will Kolanut change things without approval?',
    answer:
      'You stay in control.\n\n• Agents recommend and run flows you configure\n• Critical changes can require human approval\n• Full visibility in dashboard and mobile app\n• No black-box automation on ad spend',
  },
  {
    id: 5,
    question: 'How fast can we get value?',
    answer:
      'Most SaaS teams connect their stack in minutes.\n\n• Guided setup—no engineering sprint\n• First insights after initial data sync\n• Onboarding and campaign templates included\n• Mobile app for real-time oversight',
  },
  {
    id: 6,
    question: 'Who is Kolanut for SaaS built for?',
    answer:
      'Growth, marketing, and founder-led teams at B2B and B2C SaaS companies.\n\n• Teams drowning in tools but starving for unified action\n• Companies spending on paid acquisition who need better retention\n• Operators who want acquisition to compound into expansion',
  },
];

export const howItWorksSteps = [
  {
    Icon: Link2,
    title: 'Connect your stack',
    description:
      'Plug in Meta, Google Ads, Gmail, SMS, and your product analytics. Read-only sync into one Company Brain—under two minutes.',
    logos: ['Meta', 'Google Ads', 'Gmail', 'SMS'],
    accent: {
      icon: 'bg-gradient-to-br from-sky-100 to-sky-50 text-sky-900 ring-sky-200/90',
      card: 'border-sky-200/70 bg-gradient-to-br from-white to-sky-50/50',
    },
  },
  {
    Icon: Brain,
    title: 'Learn and personalize',
    description:
      'The Company Brain maps cohorts, lifecycle stages, and churn signals. AI agents draft flows you approve—not black-box automation.',
    logos: [],
    agents: ['Marketing agent', 'Onboarding agent', 'Support agent'],
    accent: {
      icon: 'bg-gradient-to-br from-violet-100 to-violet-50 text-violet-900 ring-violet-200/90',
      card: 'border-violet-200/70 bg-gradient-to-br from-white to-violet-50/40',
    },
  },
  {
    Icon: Rocket,
    title: 'Compound growth',
    description:
      'Campaigns, onboarding, and proactive support run on autopilot. You oversee everything from dashboard or mobile.',
    logos: [],
    actions: ['Optimize ads', 'Rescue trials', 'Save at-risk ARR'],
    accent: {
      icon: 'bg-gradient-to-br from-amber-100 to-amber-50 text-amber-950 ring-amber-200/90',
      card: 'border-amber-200/70 bg-gradient-to-br from-white to-amber-50/45',
    },
  },
];
